// Owns the user's Theme object, resolves it against the sport registry, paints
// it, and hands Mantine a matching accent scale. The copy here is the
// browser's, which paints the first frame; signed in, the account's is put
// here and a change here is saved to it (`accountPrefs.tsx`, B-245).

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { MantineProvider, createTheme, type CSSVariablesResolver } from '@mantine/core';
import {
  DEFAULT_THEME,
  ThemeSchema,
  resolveTheme,
  type ResolvedTheme,
  type Theme,
} from '@fsp/theme';
import { lookupTeamPalette } from '../sports/registry';
import { useLook } from '../data/look';
import { lookOf } from '../looks/registry';
import { applyTheme } from './applyTheme';
import { storageKey } from '../data/storage';

const THEME_KEY = storageKey('theme.v1');

interface ThemeContextValue {
  theme: Theme;
  resolved: ResolvedTheme;
  setTheme: (next: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function loadTheme(): Theme {
  try {
    const raw = localStorage.getItem(THEME_KEY);
    if (raw) return ThemeSchema.parse(JSON.parse(raw));
  } catch {
    // Fall through to the default; a corrupt cache is not worth a crash.
  }
  return DEFAULT_THEME;
}

function systemDark(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(loadTheme);
  const [dark, setDark] = useState(systemDark);
  const look = useLook();

  useEffect(() => {
    if (typeof matchMedia !== 'function') return;
    const mq = matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e: MediaQueryListEvent) => setDark(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const resolved = useMemo(
    () =>
      // A look drawn in one mode only has it, whatever the person's Mode;
      // their choice is kept for the looks that have both.
      resolveTheme(
        { ...theme, mode: lookOf(look).mode ?? theme.mode },
        { systemDark: dark, lookupTeam: lookupTeamPalette, grounds: lookOf(look).grounds },
      ),
    [theme, dark, look],
  );

  useEffect(() => {
    applyTheme(resolved, look);
  }, [resolved, look]);

  const setTheme = (next: Theme) => {
    const parsed = ThemeSchema.parse(next);
    setThemeState(parsed);
    try {
      localStorage.setItem(THEME_KEY, JSON.stringify(parsed));
    } catch {
      // Storage blocked: the theme still applies for this session.
    }
  };

  const mantineTheme = useMemo(
    () =>
      createTheme({
        primaryColor: 'accent',
        primaryShade: { light: 6, dark: 5 },
        // WORDS ON A FILLED BUTTON ARE BLACK OR WHITE, whichever reads better
        // on its color (UX_STANDARDS UX-27). Mantine set white on every accent,
        // which on a light one (the Dolphins' aqua) read 3.1:1. 0.179 is the
        // luminance where black and white read equally well.
        autoContrast: true,
        luminanceThreshold: 0.179,
        colors: {
          accent: resolved.accentScale as unknown as [
            string,
            string,
            string,
            string,
            string,
            string,
            string,
            string,
            string,
            string,
          ],
        },
        // The look's names, not its values (looks/<id>/look.css), so a second look
        // restyles every Mantine component without a line here changing.
        fontFamily: 'var(--fsp-font-body)',
        fontFamilyMonospace: 'var(--fsp-font-mono)',
        headings: {
          fontFamily: 'var(--fsp-font-display)',
          fontWeight: 'var(--fsp-weight-display)',
        },
        // Mantine's own scale was xs 2, sm 4, md 8, lg 16, xl 32 (px); each step
        // lands on the look step nearest it, so Clean draws as it did.
        radius: {
          xs: 'var(--fsp-radius-plain-xs)',
          sm: 'var(--fsp-radius-plain-xs)',
          md: 'var(--fsp-radius-plain-sm)',
          lg: 'var(--fsp-radius-plain-lg)',
          // The plain radii: Mantine does arithmetic on these, and a look's
          // own may be a hand-drawn eight-value shape no arithmetic takes.
          xl: 'var(--fsp-radius-plain-lg)',
        },
        defaultRadius: 'md',
        fontSizes: {
          xs: `${0.75 * resolved.fontScale}rem`,
          sm: `${0.875 * resolved.fontScale}rem`,
          md: `${1 * resolved.fontScale}rem`,
          lg: `${1.125 * resolved.fontScale}rem`,
          xl: `${1.5 * resolved.fontScale}rem`,
        },
      }),
    [resolved],
  );

  return (
    <ThemeContext.Provider value={{ theme, resolved, setTheme }}>
      <MantineProvider
        theme={mantineTheme}
        forceColorScheme={resolved.mode}
        cssVariablesResolver={accentWash}
      >
        {children}
      </MantineProvider>
    </ThemeContext.Provider>
  );
}

/**
 * THE ACCENT'S WASH IS THE ONE ITS WORDS WERE CHECKED ON (UX-27, B-392): a
 * soft button's and an accent chip's ground is `accent-soft`, the primary at
 * the alpha `resolveTheme` lifts `accent-text` against, where Mantine's own
 * wash was a lighter step of the scale at 15%, and Add, Bot and COMMISH read
 * 4.0:1 to 4.3:1 on it in the dark.
 */
const accentWash: CSSVariablesResolver = () => {
  // A hover a few steps deeper, as Mantine's own is.
  const wash = (hover: number) => ({
    '--mantine-color-accent-light': 'var(--fsp-accent-soft)',
    '--mantine-color-accent-light-hover': `color-mix(in srgb, var(--fsp-accent) ${hover}%, transparent)`,
  });
  return { variables: {}, light: wash(18), dark: wash(22) };
};

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside ThemeProvider');
  return ctx;
}
